import { useState, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import apiService from "../services/api";
import { normalizeProfile, saveCurrentProfile } from "../utils/profileUtils";
import webPlayerStorage from "../utils/webPlayerStorage";

const WebPlayerEditProfile = () => {
  const navigate = useNavigate();
  const { profileId } = useParams();

  const [saving, setSaving] = useState(false);
  const [profileName, setProfileName] = useState("");
  const [profileAvatar, setProfileAvatar] = useState(null);
  const [originalProfile, setOriginalProfile] = useState(null);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [availableAvatars, setAvailableAvatars] = useState([]);
  const [loadingAvatars, setLoadingAvatars] = useState(false);

  useEffect(() => {
    const loadSelectedProfile = () => {
      const loginAuth = webPlayerStorage.getLoginAuth();
      const rawProfiles = loginAuth?.profiles || [];
      const selectedProfile = rawProfiles.filter(
        (p) => p.profileId === profileId
      )?.[0];

      if (selectedProfile) {
        setOriginalProfile(selectedProfile);
        setProfileName(selectedProfile.profileName);
        setProfileAvatar(selectedProfile.avatarUrl);
      } else {
        toast.error("Profile not found");
        navigate("/web-player/profiles");
      }
    };

    loadSelectedProfile();
  }, [navigate, profileId]);

  useEffect(() => {
    if (showAvatarModal) {
      fetchAvatars();
    }
  }, [showAvatarModal]);

  const fetchAvatars = async () => {
    try {
      setLoadingAvatars(true);
      const response = await apiService.getAvatarsDirect();
      const avatarData = response.data || response || [];

      let avatars = [];

      if (Array.isArray(avatarData) && avatarData.length > 0) {
        if (typeof avatarData[0] === "string") {
          avatars = avatarData;
        } else if (avatarData[0].url || avatarData[0].image) {
          avatars = avatarData.map((item) => item.url || item.image);
        } else {
          avatars = avatarData;
        }
      }

      setAvailableAvatars(avatars);
    } catch (error) {
      console.error("Error fetching avatars:", error);
      setAvailableAvatars([]);
    } finally {
      setLoadingAvatars(false);
    }
  };

  const handleSave = async () => {
    if (!profileName.trim()) {
      toast.error("Profile name cannot be empty");

      return;
    }

    setSaving(true);
    try {
      const profileData = {
        profileName: profileName.trim(),
        avatarUrl: profileAvatar || originalProfile.avatarUrl,
      };

      await apiService.webPlayerUpdateProfile(profileId, profileData);
      const loginAuth = webPlayerStorage.getLoginAuth();
      const updatedProfiles = loginAuth.profiles.map((p) => {
        if (p.profileId === profileId) {
          return { ...p, ...profileData };
        } else {
          return p;
        }
      });

      loginAuth.profiles = updatedProfiles;
      webPlayerStorage.setLoginAuth(loginAuth);

      // Update storage with normalized profile structure
      const updatedProfile = updatedProfiles.find(
        (p) => p.profileId === profileId
      );

      if (updatedProfile) {
        // Normalize profile to ensure consistent structure
        const normalizedProfile = normalizeProfile(updatedProfile);

        // Update localStorage
        webPlayerStorage.setProfile(normalizedProfile);

        // Update sessionStorage using utility (single source of truth)
        saveCurrentProfile(normalizedProfile);

        // Dispatch custom event to notify other components of profile update
        window.dispatchEvent(
          new CustomEvent("profileUpdated", {
            detail: { profile: normalizedProfile },
          })
        );
      }

      toast.success("Profile updated successfully!");
      navigate("/web-player/profiles");
    } catch (error) {
      toast.error(error.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/web-player/profiles");
  };

  if (!originalProfile) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white" />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen flex items-center justify-center p-4">
        <motion.div
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-2xl mx-auto"
          initial={{ opacity: 0, y: 20 }}
        >
          {/* Back Button */}
          <button
            className="mb-8 text-gray-400 hover:text-white transition-colors flex items-center gap-2"
            onClick={handleCancel}
          >
            <i className="fas fa-arrow-left" />
            <span>Back to Profiles</span>
          </button>

          <div className="bg-gray-800/80 backdrop-blur-md rounded-2xl p-8">
            <h1 className="text-3xl font-light text-white mb-8 text-center">
              Edit Profile
            </h1>

            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Avatar Section */}
              <div className="flex-shrink-0">
                <div className="relative">
                  <button
                    className="relative group"
                    onClick={() => setShowAvatarModal(true)}
                  >
                    <div className="w-40 h-40 rounded-full overflow-hidden bg-gray-700 border-4 border-transparent group-hover:border-blue-400 transition-all">
                      {profileAvatar ? (
                        <img
                          alt="Profile"
                          className="w-full h-full object-cover"
                          src={profileAvatar}
                        />
                      ) : originalProfile.avatar ? (
                        <img
                          alt="Profile"
                          className="w-full h-full object-cover"
                          src={originalProfile.avatar}
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-white text-4xl font-bold">
                          {profileName.charAt(0).toUpperCase() ||
                            originalProfile.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </div>
                    <div className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg">
                      <svg
                        className="w-5 h-5 text-gray-700"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
                      </svg>
                    </div>
                  </button>
                  <p className="text-gray-400 text-sm text-center mt-3">
                    Click to change avatar
                  </p>
                </div>
              </div>

              {/* Form Section */}
              <div className="flex-1 w-full">
                <div className="mb-6">
                  <label className="block text-white text-sm font-medium mb-3">
                    PROFILE NAME
                  </label>
                  <input
                    className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-purple-500"
                    disabled={saving}
                    placeholder="Enter profile name"
                    type="text"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                  />
                </div>

                <p className="text-gray-400 text-sm mb-8">
                  This name will be displayed when selecting profiles and
                  throughout your experience.
                </p>

                {/* Action Buttons */}
                <div className="flex gap-4">
                  <button
                    className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 text-white rounded-full transition-colors"
                    disabled={saving}
                    onClick={handleCancel}
                  >
                    Cancel
                  </button>
                  <button
                    className="flex-1 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-full transition-colors"
                    disabled={!profileName.trim() || saving}
                    onClick={handleSave}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Dashboard Button */}
          <button
            className="fixed bottom-8 right-8 text-white text-sm font-medium bg-black/50 px-4 py-2 rounded-lg opacity-40 hover:opacity-100 hover:scale-105 transition-all"
            onClick={() => {
              // Clear all session data
              webPlayerStorage.clearAll();
              // Redirect to dashboard
              window.location.href = "https://dashboard.lunova.app";
            }}
          >
            Dashboard
          </button>
        </motion.div>
      </div>

      {/* Avatar Selection Modal */}
      <AnimatePresence>
        {showAvatarModal && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 flex items-center justify-center z-50"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          >
            <motion.div
              animate={{ opacity: 1, y: 0 }}
              className="w-full h-full flex flex-col p-8"
              exit={{ opacity: 0, y: 20 }}
              initial={{ opacity: 0, y: 20 }}
            >
              <h2 className="text-3xl font-light text-white mb-12 text-center">
                Choose Your Avatar
              </h2>

              <div className="flex-1 overflow-y-auto px-4">
                {loadingAvatars ? (
                  <div className="flex justify-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-8 max-w-7xl mx-auto pb-4">
                    {availableAvatars.map((avatar, index) => {
                      const avatarUrl =
                        typeof avatar === "string"
                          ? avatar
                          : avatar.url || avatar.image;

                      return (
                        <button
                          className="group relative cursor-pointer transition-transform duration-200 hover:scale-110"
                          key={avatarUrl || index}
                          onClick={() => {
                            setProfileAvatar(avatarUrl);
                            setShowAvatarModal(false);
                            toast.success("Avatar selected!");
                          }}
                        >
                          <div
                            className={`w-36 h-36 rounded-full overflow-hidden border-4 transition-colors duration-200 ${
                              profileAvatar === avatarUrl
                                ? "border-blue-400"
                                : "border-transparent group-hover:border-blue-400"
                            }`}
                          >
                            {avatarUrl ? (
                              <img
                                alt={`Avatar ${index + 1}`}
                                className="w-full h-full object-cover"
                                src={avatarUrl}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                  e.target.parentElement.innerHTML = `<div class="w-full h-full bg-gradient-to-br from-purple-400 to-purple-600 flex items-center justify-center text-white text-2xl font-bold">${
                                    index + 1
                                  }</div>`;
                                }}
                              />
                            ) : (
                              <div
                                className="w-full h-full flex items-center justify-center text-white text-2xl font-bold"
                                style={{
                                  backgroundColor: `hsl(${index * 40}, 70%, 50%)`,
                                }}
                              >
                                {index + 1}
                              </div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-center">
                <button
                  className="px-12 py-4 bg-gray-800/70 hover:bg-gray-700/70 text-white text-lg rounded-full transition-colors min-w-[200px]"
                  onClick={() => setShowAvatarModal(false)}
                >
                  Back
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default WebPlayerEditProfile;
